import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { isEqual } from 'lodash-es';
import { Observable, Subject, combineLatest } from 'rxjs';
import { delay, distinctUntilChanged, filter, startWith, take, takeUntil } from 'rxjs/operators';

import {
  Feature,
  OtherRoomCommands,
  OtherRoomProperties,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client';

import {
  CommissioningStore,
  DevicesStore,
  GatewayDeviceGroup,
  GatewayStore,
  InstallationFeaturesStore,
} from '@store';

import { ActivatedRoute } from '@angular/router';
import { RoutingService } from '@services';
import { ReferenceRoomSettingsHelper } from 'src/app/+installations/installation/refrigerant-circuit-features-panel/reference-room-settings-overlay/reference-room-settings.helper';
import { matTooltipSetter } from '../../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { InstallationService } from '../../../../services/installation.service';
import { AllAddedDevicesModalComponent } from '../../../../single-room-control/src-overlay/src-commissioning-overlay/commissioning-overview/all-added-devices-modal/all-added-devices-modal.component';
import { AllAddedRoomsModalComponent } from '../../../../single-room-control/src-overlay/src-commissioning-overlay/commissioning-overview/all-added-rooms-modal/all-added-rooms-modal.component';
import { ROOMS_FILTER_TYPE } from '../../commissioning/commissioning.model';
import { CommissioningService } from '../../commissioning/commissioning.service';

@Component({
  selector: 'app-commissioning-overview',
  templateUrl: './commissioning-overview.component.html',
  styleUrls: ['./commissioning-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningOverviewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  @ViewChildren('appListAll', { read: ElementRef })
  appListAll: QueryList<ElementRef<HTMLElement>>;

  notAssignedHCsToOtherRooms$: Observable<string[]>;
  otherRooms$: Observable<Feature<OtherRoomProperties, OtherRoomCommands>[]>;
  allAddedRoomsDialogRef: MatDialogRef<AllAddedRoomsModalComponent>;
  allAddedDevicesDialogRef: MatDialogRef<AllAddedDevicesModalComponent>;
  activeRoomsFilter: ROOMS_FILTER_TYPE = ROOMS_FILTER_TYPE.ALL;
  isAddRoomDisabled: boolean;
  isAddDeviceDisabled: boolean;
  unAssignableDevices$: Observable<GatewayDeviceGroup[]>;
  rooms$: Observable<Feature<RoomProperties, RoomCommands>[]>;
  isQueryParamChecked = false;
  highlightIndex: number;

  readonly filterType = ROOMS_FILTER_TYPE;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private rooms: Feature<RoomProperties, RoomCommands>[] = [];

  constructor(
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    public commissioningService: CommissioningService,
    public installationFeaturesStore: InstallationFeaturesStore,
    public installationService: InstallationService,
    private dialog: MatDialog,
    private commissioningStore: CommissioningStore,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private routingService: RoutingService
  ) {}

  ngOnInit(): void {
    this.installationFeaturesStore.isAddRoomDisabled$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value) => {
        this.isAddRoomDisabled = value;
      });
    this.notAssignedHCsToOtherRooms$ = this.installationFeaturesStore.notAssignedHCsToOtherRooms$.pipe(
      distinctUntilChanged(isEqual)
    );
    this.otherRooms$ = this.installationFeaturesStore.otherRooms$.pipe(
      distinctUntilChanged(isEqual)
    );
    this.commissioningStore.isAddDeviceDisabled$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value) => {
        this.isAddDeviceDisabled = value;
        this.cdr.detectChanges();
      });

    this.unAssignableDevices$ = this.gatewayStore.unAssignableDevices$.pipe(
      distinctUntilChanged(isEqual)
    );
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.appListAll.changes.pipe(startWith(this.appListAll)),
      this.installationFeaturesStore.rooms$.pipe(filter((rooms) => rooms.length > 0)),
    ])
      // add delay to let other ui elements load to achieve better ux
      .pipe(delay(500), take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe(([_list, rooms]) => {
        this.rooms = rooms;
        this.checkQueryParams();
      });
  }

  onAddRoom(isAddingDisabled: boolean): void {
    if (!isAddingDisabled) {
      this.commissioningService.openAddRoomDialog();
    }
  }

  onAddDevice(): void {
    this.commissioningService.openAddDeviceDialog();
  }

  openAllAddedRoomsDialog(): void {
    this.allAddedRoomsDialogRef = this.dialog.open(AllAddedRoomsModalComponent, {
      width: '710px',
      panelClass: 'commissioning-all-added-rooms-dialog',
      data: {},
      disableClose: true,
      autoFocus: false,
    });
  }

  setFilter(filter: ROOMS_FILTER_TYPE): void {
    this.activeRoomsFilter = filter;
  }

  otherRoomNameTrackBy(index: number): number {
    return index;
  }

  private checkQueryParams(): void {
    if (this.isQueryParamChecked) {
      return;
    }
    this.isQueryParamChecked = true;

    const targetRoomName = this.activatedRoute.snapshot.queryParams[
      ReferenceRoomSettingsHelper.queryParamRoom
    ];
    if (targetRoomName) {
      this.routingService.removeQueryParams([ReferenceRoomSettingsHelper.queryParamRoom]);
      this.scrollIntoRoomDetails(targetRoomName);
    }
  }

  private scrollIntoRoomDetails(targetRoomName: string): void {
    const matchingIndex = this.rooms.findIndex(
      (room) => room.properties.name.value === targetRoomName
    );

    if (matchingIndex >= 0) {
      this.highlightIndex = matchingIndex;
      this.cdr.markForCheck();

      const matchingRoom = this.appListAll.get(matchingIndex);

      matchingRoom.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
